import { AlertTriangle } from "lucide-react"
import { Component, type ReactNode } from "react"

interface DiffErrorBoundaryProps {
    fileName?: string
    children: ReactNode
}

interface DiffErrorBoundaryState {
    message: string | null
}

/**
 * Diff rendering runs third-party highlighting over provider-generated patches,
 * so a malformed patch can throw mid-render. One unrenderable diff must degrade
 * to a readable row instead of taking down the surrounding thread or tray.
 */
export class DiffErrorBoundary extends Component<DiffErrorBoundaryProps, DiffErrorBoundaryState> {
    state: DiffErrorBoundaryState = { message: null }

    static getDerivedStateFromError(error: unknown): DiffErrorBoundaryState {
        return { message: error instanceof Error ? error.message : "Unknown rendering error" }
    }

    componentDidCatch(error: unknown): void {
        console.error("[DiffErrorBoundary] Failed to render diff", { fileName: this.props.fileName, error })
    }

    render(): ReactNode {
        if (this.state.message === null) return this.props.children
        return (
            <div className="flex items-start gap-2 border-t border-border bg-base-200/50 px-3 py-2 text-xs text-muted">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
                <div className="min-w-0">
                    <div className="text-base-content">Could not render this diff</div>
                    <div className="mt-0.5 break-words">{this.state.message}</div>
                </div>
            </div>
        )
    }
}
